import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { GOOGLE_CALLBACK_PATH } from './GoogleSignInButton';

/**
 * Native shell lifecycle wiring. Mounts ONCE at the app root. No-ops on web.
 *
 * Handles:
 *   • Deep-link routing — tapping `https://partyresume.com/breakfast-with-aliens`
 *     in another app opens our app to that path instead of Safari/Chrome.
 *   • Android hardware back button — pop history; exit on root.
 *   • Push notification tap — navigate to the deep-link path the backend
 *     embedded in the FCM payload's `data.url`.
 *   • Status bar style — track Outside/Inside theme so the notch text is
 *     readable in both modes.
 *   • Splash screen hide — after the first paint so users don't see a
 *     white flash during JS boot.
 *   • SignalR teardown on backgrounding — drop the WebSocket when the OS
 *     suspends us so we don't drain battery (push picks up the slack).
 *
 * All plugin imports are dynamic so the web bundle doesn't try to evaluate
 * iOS/Android-only native bindings.
 */
/**
 * Turn a deep link into an in-app route.
 *
 * Two shapes arrive here and they parse differently:
 *
 *   https://partyresume.com/events/ABC   (Universal Link / App Link)
 *   com.partyresume://events/ABC         (custom scheme)
 *
 * For the custom scheme `new URL()` reads "events" as the HOST and leaves
 * pathname empty, so taking `u.pathname` alone silently dropped the route and
 * the link did nothing. Everything after the scheme is the route.
 *
 * Exported for tests.
 */
export function routeFromDeepLink(url) {
  if (!url) return null;

  // `scheme://host/path` and `scheme:/path` are both real. Google's iOS OAuth
  // client redirects to REVERSED_CLIENT_ID:/oauth2redirect — one slash — so
  // matching only '://' dropped the sign-in callback entirely.
  const colon = url.indexOf(':');
  if (colon < 0) return null;
  const scheme = url.slice(0, colon).toLowerCase();
  let rest = url.slice(colon + 1);
  if (rest.startsWith('//')) rest = rest.slice(2);
  else if (rest.startsWith('/')) rest = rest.slice(1);
  else return null;

  if (scheme === 'http' || scheme === 'https') {
    try {
      const u = new URL(url);
      const path = `${u.pathname}${u.search}${u.hash}`;
      return path && path !== '/' ? path : '/';
    } catch {
      return null;
    }
  }

  // Custom scheme: host and path are both part of the route. A leading slash
  // makes "com.partyresume:///events/ABC" and "com.partyresume://events/ABC"
  // resolve the same way.
  const trimmed = rest.replace(/^\/+/, '');
  if (!trimmed) return '/';

  const route = `/${trimmed}`;
  // Google's iOS client redirects to its own scheme at /oauth2redirect. Point it
  // at the one page that knows how to finish a Google sign-in rather than adding
  // a second route (which would also need adding to both reserved-handle lists).
  if (route.startsWith('/oauth2redirect')) {
    return route.replace('/oauth2redirect', GOOGLE_CALLBACK_PATH);
  }
  return route;
}

export default function CapacitorBootstrap() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listeners = [];
    let cancelled = false;

    (async () => {
      const { App } = await import('@capacitor/app');
      const { SplashScreen } = await import('@capacitor/splash-screen');
      const { StatusBar, Style } = await import('@capacitor/status-bar');
      const { PushNotifications } = await import('@capacitor/push-notifications');
      if (cancelled) return;

      // ── Deep links ────────────────────────────────────────────────────
      // Universal Links (iOS) / App Links (Android) arrive as full URLs;
      // strip the origin and navigate inside the SPA so React Router
      // handles them like any in-app navigation.
      const urlOpen = await App.addListener('appUrlOpen', ({ url }) => {
        const path = routeFromDeepLink(url);
        if (path) navigate(path);
      });

      // A link that COLD-STARTS the app is delivered in the launch options, and
      // depending on timing the listener above can be registered after that has
      // already been dispatched — so the tap opens the app on the landing page
      // instead of the thing that was linked. getLaunchUrl replays it.
      try {
        const launch = await App.getLaunchUrl();
        if (!cancelled && launch?.url) {
          const path = routeFromDeepLink(launch.url);
          // Only redirect off the entry route; if the app already navigated
          // (the listener won the race) don't yank the user somewhere else.
          if (path && path !== '/' && window.location.pathname === '/') navigate(path);
        }
      } catch {
        // No launch URL is the normal case — a plain app open.
      }
      listeners.push(urlOpen);

      // ── Android back button ───────────────────────────────────────────
      // Default Capacitor behavior is to exit the app on back from any
      // screen — bad UX. We pop history when we can and only exit at root.
      const backBtn = await App.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack && window.history.length > 1) window.history.back();
        else App.exitApp();
      });
      listeners.push(backBtn);

      // ── Push notification tap ─────────────────────────────────────────
      // When the user taps a push (foreground or after waking the app),
      // the OS hands us the original payload. We read `data.url` (set by
      // NativePushService.SendToUserAsync) and navigate the SPA there.
      const pushTap = await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
        const url = notification?.data?.url || '/notifications';
        navigate(url);
      });
      listeners.push(pushTap);

      // ── Status bar style ──────────────────────────────────────────────
      // Outside theme = black bg → light text. Inside = light bg → dark
      // text. We toggle on initial mount; ThemeContext changes propagate
      // via a separate effect lower in the component tree.
      try {
        const isInside = document.documentElement.classList.contains('theme-inside');
        await StatusBar.setStyle({ style: isInside ? Style.Dark : Style.Light });
      } catch {/* status bar fails on some Android versions; non-fatal */}

      // ── Splash hide ───────────────────────────────────────────────────
      // launchAutoHide is false in capacitor.config so the splash holds
      // until React mounts. Hide after a tick to ensure first paint.
      setTimeout(() => SplashScreen.hide().catch(() => {}), 200);

      // ── App state change (background / foreground) ────────────────────
      // SignalR holds an idle WebSocket; OS will suspend the WebView when
      // the app is backgrounded, but on iOS the socket can keep the radio
      // hot. We dispatch a global event the MessagesPage / MessageContext
      // can listen for to disconnect/reconnect cleanly.
      const stateChange = await App.addListener('appStateChange', ({ isActive }) => {
        window.dispatchEvent(new CustomEvent('pr:app-state', { detail: { isActive } }));
      });
      listeners.push(stateChange);
    })();

    return () => {
      cancelled = true;
      listeners.forEach(l => { try { l.remove(); } catch {} });
    };
  }, [navigate]);

  return null;
}
